import { useState } from 'react';
import styled from 'styled-components';
import { theme } from '@/styles/theme';

/**
 * Language index for the welcome screen: tracked-caps codes in the top-right
 * corner, with the brand's gold rule as the active mark (the same motif that
 * underlines the Welkom title). Purely visual for now: selecting only moves
 * the active state, it does not translate the site yet (the page stays
 * Dutch). When i18n lands, wire `onSelect` through instead.
 *
 * Deliberately framer-free: a shared-layout (layoutId) element inside the
 * welcome screen's exiting AnimatePresence tree could hang the exit animation
 * after the pills were used, leaving the invisible overlay mounted over the
 * site and swallowing every click. The underline is plain CSS instead.
 */

const LANGUAGES = [
  { code: 'nl', label: 'Nederlands' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
] as const;

type LanguageCode = (typeof LANGUAGES)[number]['code'];

const Bar = styled.div`
  position: absolute;
  top: ${theme.spacing[8]};
  right: ${theme.spacing[10]};
  z-index: 4;
  display: flex;
  align-items: baseline;
  gap: ${theme.spacing[6]};

  @media (max-width: ${theme.breakpoints.sm}) {
    top: ${theme.spacing[6]};
    right: ${theme.spacing[5]};
    gap: ${theme.spacing[5]};
  }
`;

const Item = styled.button<{ $active: boolean }>`
  position: relative;
  appearance: none;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: ${theme.spacing[1]} 0 ${theme.spacing[2]};
  font-family: ${theme.fonts.body};
  font-size: ${theme.fontSizes.xs};
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: ${theme.fontWeights.semibold};
  color: var(--kph-navy-ink, #1a1a2e);
  opacity: ${({ $active }) => ($active ? 1 : 0.45)};
  transition: opacity 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover:not(:disabled) {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid var(--kph-gold, #d4af37);
    outline-offset: 3px;
    border-radius: 2px;
  }

  /* Gold rule under the active code. */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 2px;
    background: var(--kph-gold, #d4af37);
    transform: scaleX(${({ $active }) => ($active ? 1 : 0)});
    transform-origin: left center;
    transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &::after {
      transition: none;
    }
  }
`;

export const LanguageSelect = () => {
  const [active, setActive] = useState<LanguageCode>('nl');

  return (
    <Bar role="radiogroup" aria-label="Taal / Langue / Sprache / Language">
      {LANGUAGES.map(({ code, label }) => (
        <Item
          key={code}
          type="button"
          role="radio"
          aria-checked={active === code}
          aria-label={label}
          title={label}
          lang={code}
          $active={active === code}
          onClick={() => setActive(code)}
        >
          {code}
        </Item>
      ))}
    </Bar>
  );
};
